import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import { useEffect, useState } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  adminLogout,
  adminResetPassword,
  getAdminDetails,
} from "../Login/adminAuthActions";
import {
  createPasswordValidationRegex,
  getCookie,
  getPasswordValidationMessage,
} from "../../utils/Utility";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import { adminGetPasswordConfig } from "../PasswordConfiguration/passwordConfigurationActions";
import { IPasswordConfigData } from "../PasswordConfiguration/passwordConfigurationType";

const AccountInfo = () => {
  const dispatch = useAppDispatch();
  const id = getCookie("id");

  const { adminDetails, adminDetailsData } = useAppSelector(
    (state: RootState) => state.adminAuth
  );
  const { passwordConfigData } = useAppSelector(
    (state: RootState) => state.adminPasswordConfig
  );
  const { isSuccess, calledReducerType, isError } = useAppSelector(
    (state) => state.common
  );
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState("");
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  // Add new state for password regex
  const [passwordRegex, setPasswordRegex] = useState<RegExp | null>(null);

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
    const date = new Date().toString();
    const logoutRequestBody = {
      logoutTime: date,
    };
    if (isSuccess && calledReducerType === "adminAuth/adminResetPassword") {
      dispatch(adminLogout(logoutRequestBody));
    }
  };
  // Validation patterns
  const passwordPattern =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  const handleSave = () => {
    // Logic to save the updated information
    console.log("Saved:", { name, email });
  };

  const validatePasswordForm = () => {
    let tempErrors = { ...errors };
    let isValid = true;

    // Current password validation
    if (!passwordForm.oldPassword) {
      tempErrors.oldPassword = "Current password is required";
      isValid = false;
    }

    // New password validation
    if (!passwordForm.newPassword) {
      tempErrors.newPassword = "New password is required";
      isValid = false;
    } else if (passwordConfigData) {
      const passwordError = getPasswordValidationMessage(
        passwordForm.newPassword,
        passwordConfigData
      );
      if (passwordError) {
        tempErrors.newPassword = passwordError;
        isValid = false;
      }
    }

    // Confirm password validation
    if (!passwordForm.confirmPassword) {
      tempErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      tempErrors.confirmPassword = "New password and confirmation do not match";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Validate new password as user types
    if (name === "newPassword" && passwordConfigData) {
      if (value) {
        const passwordError = getPasswordValidationMessage(
          value,
          passwordConfigData
        );
        if (passwordError) {
          setErrors((prev) => ({
            ...prev,
            newPassword: passwordError,
          }));
        }
      }

      // Also validate confirm password when new password changes
      if (passwordForm.confirmPassword) {
        if (value !== passwordForm.confirmPassword) {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "New password and confirmation do not match",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "",
          }));
        }
      }
    }

    // Validate confirm password
    if (name === "confirmPassword") {
      if (value !== passwordForm.newPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "New password and confirmation do not match",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "",
        }));
      }
    }
  };

  const handleUpdatePassword = () => {
    if (validatePasswordForm()) {
      // Add your password update logic here
      dispatch(
        adminResetPassword({
          ...passwordForm,
          email: adminDetailsData.email,
        })
      );
      // Reset form
      setPasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const handleTogglePasswordVisibility = (field: string) => {
    setShowPasswords((prev: any) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleCancelPasswordDialog = () => {
    setOpenPasswordDialog(false);
    setPasswordForm({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setErrors({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  useEffect(() => {
    if (id) {
      dispatch(getAdminDetails(id));
    }
  }, []);
  useEffect(() => {
    if (adminDetails && adminDetails?.name && adminDetails?.email) {
      setName(adminDetails?.name);
      setEmail(adminDetails?.email);
    }
  }, [adminDetails]);

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "adminAuth/adminResetPassword"
    ) {
      setMessageDialogOpen(true);
      setOpenPasswordDialog(false);
    }
  }, [isSuccess, isError, calledReducerType]);

  useEffect(() => {
    dispatch(adminGetPasswordConfig());
  }, [dispatch]);

  // Update useEffect to create regex when passwordConfigData changes
  useEffect(() => {
    if (passwordConfigData) {
      setPasswordRegex(createPasswordValidationRegex(passwordConfigData));
    }
  }, [passwordConfigData]);

  return (
    <>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <Card>
          <CardHeader title="Profile" />
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Full Name</InputLabel>
                  <OutlinedInput
                    value={name}
                    label="Full name"
                    name="fullName"
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item md={6} xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Email address</InputLabel>
                  <OutlinedInput
                    value={email}
                    label="Email address"
                    name="email"
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item md={6} xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Phone number</InputLabel>
                  <OutlinedInput label="Phone number" name="phone" type="tel" />
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              sx={{ borderRadius: "4px", mr: 1 }}
              onClick={() => setOpenPasswordDialog(true)}
            >
              Change Password
            </Button>
            <Button variant="contained" sx={{ borderRadius: "4px" }}>
              Save
            </Button>
          </CardActions>
        </Card>
      </form>

      <Dialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: "400px", // Set specific width for the dialog
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 3 }}>
          <IconButton
            sx={{
              backgroundColor: "#e6f7f5",
              width: "64px",
              height: "64px",
              mb: 2,
              "&:hover": { backgroundColor: "#e6f7f5" },
            }}
            disableRipple
          >
            <LockOutlinedIcon sx={{ color: "#00a99d", fontSize: "32px" }} />
          </IconButton>
          <div>Change Password</div>
        </DialogTitle>
        <DialogContent
          sx={{
            "&.MuiDialogContent-root": {
              paddingTop: "8px !important", // Use !important to override the default style
            },
          }}
        >
          {" "}
          {/* Added top padding */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel
              required
              sx={{
                backgroundColor: "white",
                px: 0.5, // Add horizontal padding to the label
              }}
            >
              Current Password
            </InputLabel>
            <OutlinedInput
              type={showPasswords.oldPassword ? "text" : "password"}
              name="oldPassword"
              label="Current Password"
              value={passwordForm.oldPassword}
              onChange={handlePasswordChange}
              error={!!errors.oldPassword}
              endAdornment={
                <IconButton
                  onClick={() => handleTogglePasswordVisibility("oldPassword")}
                  edge="end"
                >
                  {showPasswords.oldPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              }
            />
            {errors.oldPassword && (
              <div
                style={{ color: "red", fontSize: "0.75rem", marginTop: "3px" }}
              >
                {errors.oldPassword}
              </div>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel
              required
              sx={{
                backgroundColor: "white",
                px: 0.5,
              }}
            >
              New Password
            </InputLabel>
            <OutlinedInput
              type={showPasswords.newPassword ? "text" : "password"}
              name="newPassword"
              label="New Password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              error={!!errors.newPassword}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              }}
              endAdornment={
                <IconButton
                  onClick={() => handleTogglePasswordVisibility("newPassword")}
                  edge="end"
                >
                  {showPasswords.newPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              }
            />
            {errors.newPassword && (
              <div
                style={{ color: "red", fontSize: "0.75rem", marginTop: "3px" }}
              >
                {errors.newPassword}
              </div>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel
              required
              sx={{
                backgroundColor: "white",
                px: 0.5,
              }}
            >
              Confirm New Password
            </InputLabel>
            <OutlinedInput
              type={showPasswords.confirmPassword ? "text" : "password"}
              name="confirmPassword"
              label="Confirm New Password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              error={!!errors.confirmPassword}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              }}
              endAdornment={
                <IconButton
                  onClick={() =>
                    handleTogglePasswordVisibility("confirmPassword")
                  }
                  edge="end"
                >
                  {showPasswords.confirmPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              }
            />
            {errors.confirmPassword && (
              <div
                style={{ color: "red", fontSize: "0.75rem", marginTop: "3px" }}
              >
                {errors.confirmPassword}
              </div>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: "center" }}>
          <Button
            variant="outlined"
            onClick={handleCancelPasswordDialog}
            sx={{ minWidth: "100px", mr: 1, borderRadius: "4px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdatePassword}
            sx={{
              minWidth: "100px",
              backgroundColor: "#00a99d",
              borderRadius: "4px",
            }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </>
  );
};

export default AccountInfo;
